import React, { useMemo } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PenLine } from 'lucide-react';

const COLORS = ['#6B4C9A','#10B981','#F59E0B','#3B82F6','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316','#84CC16'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[160px]">
      <p className="font-semibold text-gray-800 text-sm mb-1 truncate">{d.name}</p>
      <p className="text-xs text-indigo-600 font-bold">{d.count} US en raffinage</p>
    </div>
  );
};

const RaffinageByProjectChart = ({ usStageGroups = [], colorMap = {} }) => {
  const chartData = useMemo(() => {
    const map = {};
    usStageGroups.forEach(g => {
      const key = g.projectId;
      if (!map[key]) map[key] = { projectId: key, name: g.projectName || `Projet ${key}`, count: 0 };
      map[key].count += g.count;
    });
    return Object.values(map).filter(d => d.count > 0).sort((a, b) => b.count - a.count).slice(0, 12);
  }, [usStageGroups]);

  const total = chartData.reduce((s, d) => s + d.count, 0);

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center justify-center min-h-[200px]">
        <PenLine className="w-10 h-10 text-gray-200 mb-3" />
        <p className="text-sm text-gray-400">Aucune US en raffinage</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
          <PenLine className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Raffinage par projet</h3>
          <p className="text-xs text-gray-500">{total} US au total</p>
        </div>
      </div>

      <div style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#374151' }}
              axisLine={false}
              tickLine={false}
              angle={-35}
              textAnchor="end"
              interval={0}
              tickFormatter={v => v.length > 16 ? v.slice(0, 16) + '…' : v}
            />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chartData.map((d, i) => <Cell key={i} fill={colorMap[d.projectId] || COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RaffinageByProjectChart;
